import { useCallback, useEffect, useRef, useState } from 'react';
import { getIncidentRoster } from '../Services/IncidentService';
import { IncidentRosterPerson, PersonStatus } from '../Types/Incident';
import { findLinkedAccountingPeople } from '../Utils/linkedAccounting';

interface Options {
  incidentId?: string;
  disabled: boolean;
  queueStatus: (personId: string, status: PersonStatus) => void;
  getEffectiveStatus: (person: IncidentRosterPerson) => PersonStatus;
}

export interface LinkedAccountingRequest {
  personId: string;
  person?: IncidentRosterPerson;
  matches: IncidentRosterPerson[];
  loading: boolean;
  error?: string;
}

export const useLinkedAccounting = (options: Options) => {
  const [request, setRequest] = useState<LinkedAccountingRequest | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const generation = useRef(0);
  const invalidate = useCallback(() => { generation.current++; }, []);

  const cancel = useCallback(() => {
    invalidate();
    setRequest(null);
  }, [invalidate]);

  useEffect(() => {
    cancel();
    return invalidate;
  }, [options.incidentId, options.disabled, cancel, invalidate]);

  const discover = useCallback(async (personId: string) => {
    const { incidentId, disabled } = optionsRef.current;
    if (!incidentId || disabled) return;
    const currentGeneration = ++generation.current;
    setRequest({ personId, matches: [], loading: true });
    try {
      // Never use the visible list: the counterpart can be in another view, scope or page.
      const result = await getIncidentRoster(incidentId, { scope: 'full' });
      if (generation.current !== currentGeneration || optionsRef.current.disabled) return;
      const person = result.people.find((p) => p.personId === personId);
      if (!person) throw new Error('Record unavailable');
      const matches = findLinkedAccountingPeople(person, result.people)
        .filter((p) => optionsRef.current.getEffectiveStatus(p) !== 'accounted');
      if (matches.length === 0) {
        optionsRef.current.queueStatus(personId, 'accounted');
        setRequest(null);
      } else {
        setRequest({ personId, person, matches, loading: false });
      }
    } catch {
      if (generation.current !== currentGeneration || optionsRef.current.disabled) return;
      setRequest({ personId, matches: [], loading: false,
        error: 'Unable to check the complete incident roster for matching records. No changes have been queued. Retry when connected.' });
    }
  }, []);

  const changeStatus = useCallback((personId: string, status: PersonStatus) => {
    if (optionsRef.current.disabled || !optionsRef.current.incidentId) return;
    if (status === 'accounted') {
      void discover(personId);
    } else {
      cancel();
      optionsRef.current.queueStatus(personId, status);
    }
  }, [cancel, discover]);

  const confirm = useCallback((selectedPersonIds: string[]) => {
    if (!request || request.loading || request.error || optionsRef.current.disabled) return;
    const selected = new Set(selectedPersonIds);
    optionsRef.current.queueStatus(request.personId, 'accounted');
    request.matches.forEach((person) => {
      if (selected.has(person.personId) && optionsRef.current.getEffectiveStatus(person) !== 'accounted') {
        optionsRef.current.queueStatus(person.personId, 'accounted');
      }
    });
    cancel();
  }, [request, cancel]);

  const retry = useCallback(() => {
    if (request) void discover(request.personId);
  }, [request, discover]);

  return { request, changeStatus, confirm, cancel, retry };
};
